import { TableCellHeight } from '@busybusy/webapp-react-ui/dist/components/Table/types/types';
import classNames from 'classnames';
import { EmptyState, LazyLoader, PanelContent } from 'components';
import MemberTimeEntryDialog from 'components/domain/time-entry/dialog/MemberTimeEntryDialog/MemberTimeEntryDialog';
import TimeActionsFormDialog from 'components/domain/time-entry/time-actions-form/TimeActionsFormDialog/TimeActionsFormDialog';
import { ITimeActionsFormData } from 'components/domain/time-entry/time-actions-form/hooks/useTimeActionsForm';
import { ITimeOffFormData } from 'components/domain/time-off/form/CreateTimeOffForm/CreateTimeOffForm';
import TimeOffFormDialog from 'components/domain/time-off/form/TimeOffFormDialog/TimeOffFormDialog';
import TimeCardReportMemberHeader from 'containers/timesheets/TimeCardReportMemberHeader/TimeCardReportMemberHeader';
import TimeCardReportTable from 'containers/timesheets/TimeCardReportTable/TimeCardReportTable';
import TimesheetPrintHeader from 'containers/timesheets/TimesheetPrintHeader/TimesheetPrintHeader';
import { useOpenable } from 'hooks';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import { DateTime } from 'luxon';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IMember } from 'types';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { numberOfDaysBetween } from 'utils/dateUtils';
import { t } from 'utils/localize';
import SignOffReportDialog from '../SignOffReportDialog/SignOffReportDialog';
import TimesheetPrintSignature from '../TimesheetPrintSignature/TimesheetPrintSignature';
import './TimeCardReport.scss';
import { TimeCardReportContext } from './context/TimeCardReportContext';

export interface ITimeCardReportProps {
  employeeIds?: string[] | null;
  timeRange: ITimeRange<DateTime>;
  // Callback to handle refreshing previous data set
  onDataChange?: (members: string[]) => void;
  timeRangeType: TimeRangeType;
  scroller?: HTMLElement | 'self';
  onSignatureComplete?: (memberId: string) => void;
  rowHeight?: TableCellHeight;
}

const TimeCardReport = ({
  employeeIds,
  scroller,
  timeRange,
  timeRangeType,
  onDataChange,
  onSignatureComplete,
  rowHeight,
}: ITimeCardReportProps) => {
  const { members, loadedAll, didLoad, clearData, getMoreMembers, refreshMembersData, populateData } =
    useContext(TimeCardReportContext);

  const renderSize = numberOfDaysBetween(timeRange.startTime, timeRange.endTime) > 20 ? 1 : 3;
  const memberSettings = useMemberSettings();

  const [dialogMember, setDialogMember] = useState<IMember | null>(null);
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);

  const viewEntriesDialog = useOpenable({ onClose: clearDialogSettings });
  const timeEntryFormDialog = useOpenable({ onClose: clearDialogSettings });
  const signOffReportDialog = useOpenable({ onClose: clearDialogSettings });
  const addTimeOffDialog = useOpenable({ onClose: clearDialogSettings });

  const memberIds = useMemo(() => (dialogMember ? [dialogMember.id] : []), [dialogMember]);

  const printOptions = memberSettings?.web?.features?.timeCards?.printOptions;

  useEffect(() => {
    clearData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange.startTime.toSeconds(), timeRange.endTime.toSeconds(), employeeIds]);

  useEffect(() => {
    // we are only observing members on purpose, because we only want to call populate once we have our updated members call
    // which will include all of the aggregate data
    populateData(timeRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);

  function clearDialogSettings() {
    setDialogMember(null);
    setSelectedDate(null);
  }

  const onAddTimeEntry = useCallback((date: DateTime, member: IMember) => {
    setDialogMember(member);
    setSelectedDate(date);
    timeEntryFormDialog.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddTimeOff = useCallback((date: DateTime, member: IMember) => {
    setDialogMember(member);
    setSelectedDate(date);
    addTimeOffDialog.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onViewSignOff = useCallback((date: DateTime, member: IMember) => {
    setDialogMember(member);
    setSelectedDate(date);
    signOffReportDialog.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onViewTimeEntries = useCallback((date: DateTime, member: IMember) => {
    setDialogMember(member);
    setSelectedDate(date);
    viewEntriesDialog.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onViewMemberEntries(member: IMember) {
    setDialogMember(member);
    viewEntriesDialog.open();
  }

  function onViewSignOffs(member: IMember) {
    setDialogMember(member);
    signOffReportDialog.open();
  }

  const getMembers = (cursor?: string): Promise<IMember[]> => {
    return getMoreMembers(timeRange, 2, employeeIds ?? null, cursor);
  };

  const onSignatureCurried = (memberId: string) => () => onSignatureComplete?.(memberId);

  const renderRow = useCallback(
    (member: IMember, index: number) => {
      return (
        <div
          key={member.id}
          className={classNames(
            index !== 0 ? 'mt-8' : '',
            'timecard-report-container',
            printOptions?.pageBreaks ? 'page-break' : ''
          )}
          data-testid={member.id}
        >
          {printOptions?.pageBreaks && index !== 0 && (
            <TimesheetPrintHeader title={t('Time Cards')} timeRange={timeRange} />
          )}
          <TimeCardReportMemberHeader
            member={member}
            onViewEntries={onViewMemberEntries}
            onViewSignOff={onViewSignOffs}
            isExpanded={false}
          />

          <TimeCardReportTable
            member={member}
            timeRange={timeRange}
            scroller={scroller !== 'self' ? scroller : undefined}
            timeRangeIsPayPeriod={timeRangeType === TimeRangeType.PAY_PERIOD}
            onAddTimeEntry={onAddTimeEntry}
            onAddTimeOff={onAddTimeOff}
            onViewSignOff={onViewSignOff}
            onViewTimeEntries={onViewTimeEntries}
            onSignatureComplete={onSignatureCurried(member.id)}
            rowHeight={rowHeight}
          />
          {printOptions?.position === 'at-end-of-document' ? (
            members.length - 1 === index && <TimesheetPrintSignature />
          ) : (
            <TimesheetPrintSignature />
          )}
          {printOptions?.pageBreaks && members.length - 1 !== index && <div className="time-card-page-break" />}
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scroller, timeRangeType, timeRange, printOptions, rowHeight]
  );

  function addTimeEntry(entryData: ITimeActionsFormData) {
    refreshData(entryData.members);
    timeEntryFormDialog.close();
  }

  function refreshData(memberList: string[]) {
    if (onDataChange) {
      onDataChange(memberList);
    }

    setTimeout(() => {
      refreshMembersData(memberList, timeRange);
    }, 1000);
  }

  function onTimeOffSubmit(data?: ITimeOffFormData | undefined) {
    if (data) {
      refreshData(data.members);
    }
    addTimeOffDialog.close();
  }

  const onSignOffUpdate = () => {
    refreshData(memberIds);
  };

  const range = useMemo(() => {
    return selectedDate ? { startTime: selectedDate.startOf('day'), endTime: selectedDate.endOf('day') } : timeRange;
  }, [selectedDate, timeRange]);

  return (
    <>
      <TimesheetPrintHeader title={t('Time Cards')} timeRange={timeRange} />
      <PanelContent className="timecard-report">
        <LazyLoader
          data={members}
          sectionSize={2}
          offset={200}
          renderSize={renderSize}
          renderRow={renderRow}
          getData={getMembers}
          loadedAll={loadedAll}
          didLoad={didLoad}
          scroller={scroller}
        />
        {loadedAll && members.length === 0 && <EmptyState title={t('No employees')} />}

        {viewEntriesDialog.isOpen && (
          <MemberTimeEntryDialog
            isOpen={viewEntriesDialog.isOpen}
            onClose={viewEntriesDialog.close}
            timeRange={range}
            memberIds={memberIds}
            timeRangeType={selectedDate ? TimeRangeType.DAILY : timeRangeType}
            onDataChange={refreshData}
          />
        )}
        <TimeActionsFormDialog
          type={'add'}
          isOpen={timeEntryFormDialog.isOpen}
          onClose={timeEntryFormDialog.close}
          onSubmit={addTimeEntry}
          memberIds={memberIds}
          currentDate={selectedDate ? selectedDate.toLocal() : timeRange.endTime}
          onDelete={addTimeEntry}
        />
        <SignOffReportDialog
          isOpen={signOffReportDialog.isOpen}
          onClose={signOffReportDialog.close}
          timeRange={range}
          memberIds={memberIds}
          timeRangeType={selectedDate ? TimeRangeType.DAILY : timeRangeType}
          onDataChange={onSignOffUpdate}
        />
        <TimeOffFormDialog
          memberIds={memberIds}
          currentDate={selectedDate ? selectedDate.toLocal().startOf('day') : timeRange.endTime}
          isOpen={addTimeOffDialog.isOpen}
          onClose={addTimeOffDialog.close}
          onSubmit={onTimeOffSubmit}
        />
      </PanelContent>
    </>
  );
};

export default TimeCardReport;
